import { noop, set } from 'lodash/fp';
import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { updateAuthTenantName } from '@portals/redux/actions/auth';
import { toastrError, toastrSuccess } from '@portals/redux/actions/toastr';
import { TenantType } from '@portals/types';

import { OPERATOR_API_URL } from './partners.constants';
import {
  partnerCustomersQueryKeys,
  PARTNERS_API_URL,
  PARTNERS_CUSTOMER_API_URL,
} from './partners.constants';
import { UseCreatePartnerParams } from './partners.types';
import { useApiQuery, useClearDataCache } from '../../hooks';
import { ServerError } from '../../types';
import { fetchApiRequest, useRequestOptions } from '../../utils';
import {
  ConfigPartnerType,
  ConfigType,
  configurationQueryKeys,
  useConfig,
} from '../configuration';
import { CustomerType } from '../monitoring';
import {
  DomainChangeType,
  PortalConfigType,
  useRequestDomainUpdate,
  useUpdatePortalConfig,
} from '../portal-config';

export function useUpdatePartner() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: config } = useConfig();
  const { url, options } = useRequestOptions({
    url: `${PARTNERS_API_URL}/${config?.[TenantType.Partner]?.id}`,
    method: 'PATCH',
  });

  return useMutation({
    mutationFn: (name: string) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify({ name }),
      });
    },
    onSuccess: async (response: void, name) => {
      queryClient.setQueryData<ConfigType>(
        configurationQueryKeys.config,
        (currConfig) => {
          return set(
            [TenantType.Partner, 'name'],
            name,
            currConfig as ConfigType
          );
        }
      );

      dispatch(updateAuthTenantName(name));
    },
  });
}

interface UseUpdatePartnerInfoParams {
  display_name?: string;
  logo_url?: string;
}

export function useUpdatePartnerInfo() {
  const clearDataCache = useClearDataCache();
  const dispatch = useDispatch();

  const { data: config } = useConfig();
  const { url, options } = useRequestOptions({
    url: `${PARTNERS_API_URL}/${config?.[TenantType.Partner]?.id}`,
    method: 'PATCH',
  });

  return useMutation<
    ConfigPartnerType,
    ServerError,
    UseUpdatePartnerInfoParams
  >({
    mutationFn: (info) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify(info),
      });
    },
    onSuccess: () => {
      clearDataCache([configurationQueryKeys.config]);
      dispatch(toastrSuccess('Partner info updated successfully'));
    },
    onError: ({ error }: any) => {
      dispatch(toastrError(error));
    },
  });
}

export function useUpdateDomain({
  onOnXyteDomainChanged,
  onOnXyteDomainChangeError,
  onCustomDomainRequested,
  onLogEvent = noop,
}: {
  onOnXyteDomainChanged: ({ name }: { name: string }) => void;
  onOnXyteDomainChangeError: (error: any) => void;
  onCustomDomainRequested?: (portalConfig: PortalConfigType) => void;
  onLogEvent?: (savedDomain: string) => void;
}) {
  const {
    mutateAsync: requestDomainUpdate,
    isLoading: isRequestingDomainUpdate,
  } = useRequestDomainUpdate();
  const { mutateAsync: updatePartnerName, isLoading: isUpdatingPartnerName } =
    useUpdatePartner();
  const { mutateAsync: updatePortalConfig, isLoading: isUpdatingPortalConfig } =
    useUpdatePortalConfig();

  const onUpdate = useCallback(
    async ({
      onXyteDomain,
      customDomain,
      domainChangeType,
    }: {
      onXyteDomain: string;
      customDomain?: string;
      domainChangeType: DomainChangeType;
    }) => {
      if (domainChangeType === DomainChangeType.OnXyte) {
        try {
          await updatePartnerName(onXyteDomain);

          onOnXyteDomainChanged({ name: onXyteDomain });
          onLogEvent(onXyteDomain);
        } catch ({ error }) {
          onOnXyteDomainChangeError(error);
        }
      } else {
        if (onCustomDomainRequested && customDomain) {
          await requestDomainUpdate({ domain: customDomain });
          const updatedPortalConfig = await updatePortalConfig({
            updatedConfig: {
              candidate_domain: customDomain,
            },
          });

          onCustomDomainRequested(updatedPortalConfig);
          onLogEvent(customDomain);
        }
      }
    },
    [
      onOnXyteDomainChangeError,
      onCustomDomainRequested,
      onOnXyteDomainChanged,
      onLogEvent,
      requestDomainUpdate,
      updatePartnerName,
      updatePortalConfig,
    ]
  );

  return {
    onUpdate,
    isLoading:
      isRequestingDomainUpdate ||
      isUpdatingPartnerName ||
      isUpdatingPortalConfig,
  };
}

export function useCreatePartner() {
  const dispatch = useDispatch();
  const { url, options } = useRequestOptions({
    url: OPERATOR_API_URL,
    method: 'POST',
  });

  return useMutation({
    mutationFn: (data: UseCreatePartnerParams) =>
      fetchApiRequest(url, {
        ...options,
        body: JSON.stringify(data),
      }),
    onError: (error: { error?: string }) => {
      if (error?.error) {
        dispatch(toastrError(error.error));
      }
    },
  });
}

export function useCustomers() {
  return useApiQuery<CustomerType[]>(
    PARTNERS_CUSTOMER_API_URL,
    partnerCustomersQueryKeys.all
  );
}
